import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaGoogle } from 'react-icons/fa';
import './AuthPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Get package data from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const packageData = urlParams.get('plan') ? {
    plan: urlParams.get('plan'),
    duration: urlParams.get('duration'),
    price: parseInt(urlParams.get('price')) || 0
  } : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (authError) {
      setAuthError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setLoading(true);
    setAuthError('');
    
    try {
      const user = await login(formData.email, formData.password);
      // Check if user is admin and redirect accordingly
      if (user.email === 'admin@skylinebackpackers.in' && user.isAdmin) {
        navigate('/admin/dashboard');
      } else {
        // Check if there's package data in URL parameters
        if (packageData) {
          // Redirect to booking page with the package data from URL
          navigate('/booking', { state: { package: packageData } });
        } else {
          // Redirect to home page after successful login
          navigate('/');
        }
      }
    } catch (error) {
      setAuthError(error.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setAuthError('');
    
    try {
      await signInWithGoogle();
      // Check if there's package data in URL parameters
      if (packageData) {
        // Redirect to booking page with the package data from URL
        navigate('/booking', { state: { package: packageData } });
      } else {
        // For Google login, we'll redirect to home page
        // Admins using Google login would need to use email/password
        navigate('/');
      }
    } catch (error) {
      setAuthError(error.message || 'Failed to login with Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <h1 className="auth-title">Welcome Back</h1>
              <p className="auth-subtitle">Sign in to your account</p>
            </div>
            
            <form className="auth-form" onSubmit={handleSubmit}>
              {authError && <div className="error-message" style={{ textAlign: 'center', marginBottom: '1rem' }}>{authError}</div>}
              
              <div className="auth-form-group">
                <label htmlFor="email" className="auth-form-label">Email</label>
                <FaUser className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="auth-form-input"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              
              <div className="auth-form-group">
                <label htmlFor="password" className="auth-form-label">Password</label>
                <FaLock className="input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className="auth-form-input"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>
              
              <button 
                type="submit" 
                className="auth-button"
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
              
              <div className="divider">
                <span>or</span>
              </div>
              
              <button 
                type="button" 
                className="google-button"
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                <FaGoogle className="google-icon" /> 
                Sign in with Google
              </button>
            </form>
            
            <div className="auth-footer">
              <p>Don't have an account? <Link to="/signup" className="auth-link">Sign Up</Link></p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default LoginPage;
